import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetDocumentsQuery, useLazyGetCurrentUserQuery } from 'api/main';
import {
    useSendIdVerificationMutation,
    useStartSecondIdVerificationMutation,
} from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { docTypes, stepIDs, stepStatus } from 'utils/constants';
import {
    getDocumentAvailability,
    getDocumentNameByType,
    setTunnelChangesHandlers,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import TunnelStep from '../common/TunnelStep';
import { Iframe, SubmitButton, InfoButton, DocumentLink, Loader } from 'components';
import { Typography } from '@mui/material';

const SecondIDCheck = props => {
    const { userStep, handleNextStep } = props;

    const [
        startSecondIdVerification,
        { isLoading: startLoader, data: jumioSession, isUninitialized },
    ] = useStartSecondIdVerificationMutation();
    const [sendIdVerification, { isLoading: sendLoader }] = useSendIdVerificationMutation();

    const [getCurrentUser, { isFetching: userLoading }] = useLazyGetCurrentUserQuery();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const loggingOut = useSelector(userSelectors.getLogoutLoader);

    const [frameError, setFrameError] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const stepCompleted = useMemo(() => userStep?.status === stepStatus.COMPLETED, [userStep]);
    const stepVerified = useMemo(() => userStep?.status === stepStatus.VERIFIED, [userStep]);
    const stepIncomplete = useMemo(() => userStep?.status === stepStatus.INCOMPLETE, [userStep]);
    const stepFailed = useMemo(() => userStep?.status === stepStatus.FAILED, [userStep]);
    const filteredAlerts = useMemo(() => userStep?.alerts.filter(a => !a.solved) || [], [userStep]);
    const showIframe = useMemo(
        () =>
            (jumioSession &&
                !frameError &&
                !stepCompleted &&
                !retrying &&
                (!userStep || stepIncomplete)) ||
            (jumioSession && retrying && !frameError && !!userStep),
        [frameError, jumioSession, stepCompleted, retrying, userStep, stepIncomplete]
    );

    const isDocumentAvailable = useMemo(() => {
        return getDocumentAvailability(
            tunnelSubscription?.userTunnelSteps,
            stepIDs.SECOND_ID_CHECK
        );
    }, [tunnelSubscription]);

    const { data: documents = [], isLoading: documentsLoader } = useGetDocumentsQuery(
        {
            belongsTo: internalUser.id,
            type: docTypes.SECONDARY_ID,
        },
        { skip: !isDocumentAvailable }
    );

    const handleMessage = useCallback(
        e => {
            if (!e.data || typeof e.data !== 'string' || !e.origin.includes('jumio')) return;
            try {
                const data = window.JSON.parse(e.data);
                const { value } = data.payload;

                if (value === 'loaded' && !frameLoaded) {
                    setFrameLoaded(true);
                }

                if (value === 'success' && !(stepCompleted || stepVerified)) {
                    sendIdVerification({
                        callback: () => handleNextStep(true),
                        stepId: stepIDs.SECOND_ID_CHECK,
                    });
                }
                if (value === 'error' || value === 'redirectError') {
                    setFrameError(true);
                }
            } catch (error) {
                console.log(error);
            }
        },
        [sendIdVerification, handleNextStep, frameLoaded, stepCompleted, stepVerified]
    );

    useEffect(() => {
        getCurrentUser();
    }, [getCurrentUser]);

    useEffect(() => {
        if (isUninitialized && (!userStep || stepIncomplete) && !loggingOut) {
            startSecondIdVerification();
        }
    }, [
        startSecondIdVerification,
        isUninitialized,
        userStep,
        stepIncomplete,
        startLoader,
        loggingOut,
    ]);

    useEffect(() => {
        if (stepCompleted || stepVerified) {
            return;
        }
        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage, stepCompleted, stepVerified]);

    const handleResetFrame = useCallback(() => {
        startSecondIdVerification();
        setFrameError(false);
        setRetrying(true);
    }, [startSecondIdVerification]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => true,
                frameLoaded
            ),
        [frameLoaded]
    );

    return (
        <TunnelStep
            maxWidth={stepCompleted || stepVerified ? undefined : 1010}
            title={texts.SECOND_ID_CHECK}
        >
            {stepCompleted && !frameError && (
                <Typography align="center">{texts.ATTEND_PROCESS}</Typography>
            )}
            {showIframe && (
                <Loader loading={startLoader || sendLoader || userLoading || documentsLoader}>
                    <React.Fragment>
                        <InfoButton
                            sx={{ float: 'right' }}
                            disableMarginBottom
                            title={texts.ID_DOCUMENT_VERIFICATION_TITLE}
                            content={
                                <React.Fragment>
                                    <Typography gutterBottom>
                                        {texts.ID_DOCUMENT_VERIFICATION_TEXT_1}
                                    </Typography>
                                    <Typography gutterBottom>
                                        {texts.ID_DOCUMENT_VERIFICATION_TEXT_2}
                                    </Typography>
                                </React.Fragment>
                            }
                        />
                        <Iframe title={texts.SECOND_ID_CHECK} src={jumioSession.redirectUrl} />
                    </React.Fragment>
                </Loader>
            )}
            {!startLoader &&
                !sendLoader &&
                !userLoading &&
                !documentsLoader &&
                !retrying &&
                (stepVerified || stepFailed) && (
                    <React.Fragment>
                        {frameError && <Typography align="center">{texts.FRAME_ERROR}</Typography>}
                        {!!filteredAlerts.length && (
                            <Typography variant="body2" align="center">
                                {texts.DOCUMENT_VERIFIE_AVEC_INCOHERENCES}
                            </Typography>
                        )}
                        {stepVerified && !filteredAlerts.length && (
                            <Typography align="center">{texts.DOCUMENT_VERIFIE}</Typography>
                        )}
                        {documents[0] && (
                            <DocumentLink
                                isDocumentAvailable={isDocumentAvailable}
                                document={documents[0]}
                                label={getDocumentNameByType(
                                    documents[0]?.type,
                                    documents[0]?.subtype
                                )}
                            />
                        )}
                        {(frameError || filteredAlerts.length !== 0 || stepFailed) && (
                            <SubmitButton text={texts.RETRY} onClick={handleResetFrame} />
                        )}
                    </React.Fragment>
                )}
        </TunnelStep>
    );
};

export default SecondIDCheck;
